import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { RichTextView } from "@/components/common/RichTextEditor";
import { PortalTaskExtras } from "@/components/common/TaskSubmissions";
import { Button } from "@/components/ui/button";
import { PortalStatusSelect } from "@/components/portal/PortalStatusSelect";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ClipboardList, CheckCircle2, Clock, MessageSquare, CalendarClock, Send, Hourglass, CalendarRange, AlertTriangle, ArrowLeft, ArrowRight, Upload, Info, Users, GraduationCap, Plus } from "lucide-react";
import { splitTasks, titleOf, fmtDate, STATUS_LABEL } from "@/pages/portal/portalUtils";
import { PersonalTasks, NewTaskDialog, NewProjectDialog } from "@/pages/portal/PortalPersonal";
import { TaskTraining } from "@/pages/portal/TaskTraining";

const TABS = [
  { key: "mine", label: "Current", Icon: Clock, empty: "You're caught up — there are no actions waiting on you right now." },
  { key: "attention", label: "Needs Attention", Icon: AlertTriangle, empty: "Nothing needs attention." },
  { key: "later", label: "Upcoming", Icon: CalendarRange, empty: "No upcoming tasks yet." },
  { key: "submitted", label: "Submitted", Icon: Hourglass, empty: "Nothing waiting for review." },
  { key: "done", label: "Completed", Icon: CheckCircle2, empty: "Completed tasks will appear here." },
];

const STATUS_STYLE = {
  needs_attention: "bg-rose-50 text-rose-700 border-rose-200", submitted: "bg-sky-50 text-[#006090] border-sky-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200", in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  planned: "bg-slate-50 text-slate-500 border-slate-200", not_started: "bg-slate-50 text-slate-600 border-slate-200",
};

export const StatusPill = ({ t, testId }) => (
  <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 ${STATUS_STYLE[t.status] || STATUS_STYLE.not_started}`} data-testid={testId}>
    {t.completed_by_district ? "Completed by District Team" : STATUS_LABEL[t.status] || t.status}
  </span>
);

export default function PortalTasks({ initialOpenId = null }) {
  const [tasks, setTasks] = useState(null);
  const [openId, setOpenId] = useState(initialOpenId);
  const [tab, setTab] = useState("mine");
  const [newTask, setNewTask] = useState(false);
  const [newProject, setNewProject] = useState(false);
  const [personalKey, setPersonalKey] = useState(0);
  const [trainingIds, setTrainingIds] = useState(new Set());
  const load = useCallback(() => {
    api.get("/portal/tasks").then((r) => setTasks(r.data.tasks || [])).catch(() => setTasks([]));
    api.get("/portal/training-available").then((r) => setTrainingIds(new Set(r.data.task_ids || []))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tasks && !initialOpenId) {
      const g = splitTasks(tasks);
      if (g.attention.length) setTab("attention");
    }
  }, [tasks, initialOpenId]);

  if (tasks === null) return <p className="text-sm text-slate-400">Loading your tasks…</p>;
  if (openId) return <TaskDetail taskId={openId} onBack={() => { setOpenId(null); load(); }} />;
  const groups = splitTasks(tasks);
  const cur = TABS.find((t) => t.key === tab);

  return (
    <div className="space-y-6" data-testid="portal-tasks">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">My Tasks</h1>
          <p className="mt-1 text-sm text-slate-600">Tasks you are responsible for. Click a task to update it.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setNewTask(true)} data-testid="portal-tasks-new-task" className="rounded-full bg-[#006090] hover:bg-[#004E75]"><Plus className="w-4 h-4 mr-1" />New Task</Button>
          <Button variant="outline" onClick={() => setNewProject(true)} data-testid="portal-tasks-new-project" className="rounded-full"><Plus className="w-4 h-4 mr-1" />New Project</Button>
        </div>
      </div>
      <Summary groups={groups} />
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 -mb-3">Program Tasks</p>
      <div className="flex gap-1 overflow-x-auto tr-scroll border-b border-slate-200" role="tablist" data-testid="portal-tasks-tabs">
        {TABS.filter((t) => t.key !== "attention" || groups.attention.length > 0).map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`portal-tasks-tab-${t.key}`}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#006090] text-[#006090]" : "border-transparent text-slate-500 hover:text-slate-800"} ${t.key === "attention" ? "text-rose-600" : ""}`}>
            <t.Icon className="w-4 h-4" />{t.label}<span className="text-xs text-slate-400">{groups[t.key].length}</span>
          </button>
        ))}
      </div>
      <div data-testid={`portal-tasks-panel-${tab}`}>
        {tab === "later" && <p className="text-xs text-slate-500 mb-3 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" />These aren't active yet. They'll move to Current as you progress through your program.</p>}
        {groups[tab].length === 0 ? <Empty text={cur.empty} district={groups.district} tab={tab} />
          : tab === "later" ? <ByPhase items={groups.later} onOpen={setOpenId} trainingIds={trainingIds} />
          : <div className="space-y-3">{groups[tab].map((t) => <TaskCard key={t.id} task={t} training={trainingIds.has(t.id)} onOpen={t.read_only ? null : () => setOpenId(t.id)} />)}</div>}
      </div>
      <PersonalTasks reloadKey={personalKey} />
      <NewTaskDialog open={newTask} onClose={() => setNewTask(false)} onSaved={() => { setNewTask(false); setPersonalKey((k) => k + 1); }} />
      <NewProjectDialog open={newProject} onClose={() => setNewProject(false)} onSaved={() => { setNewProject(false); toast.success("Find it under My Projects"); }} />
    </div>
  );
}

function Summary({ groups }) {
  const cells = [["Your actions", groups.mine.length, "text-[#006090]"], ["Needs attention", groups.attention.length, "text-rose-600"],
    ["Upcoming", groups.later.length, "text-slate-700"], ["Completed", groups.done.length, "text-emerald-600"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-tasks-summary">
      {cells.map(([l, n, c]) => (
        <div key={l} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <p className={`font-display text-2xl font-bold ${c}`}>{n}</p><p className="text-xs text-slate-500">{l}</p>
        </div>
      ))}
    </div>
  );
}

function Empty({ text, district, tab }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="portal-tasks-empty">
      <ClipboardList className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="font-display font-semibold text-slate-800">{tab === "mine" ? "You're caught up" : text}</p>
      {tab === "mine" && <p className="text-sm text-slate-500 mt-1">{district[0] ? `Your district team is currently working on: ${district[0].title}. We'll let you know when your next step is ready.` : text}</p>}
    </div>
  );
}

const TrainingBadge = ({ id }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 bg-teal-50 text-teal-700 border-teal-200" data-testid={`portal-task-training-badge-${id}`}>
    <GraduationCap className="w-3 h-3" />Training available
  </span>
);

function ByPhase({ items, onOpen, trainingIds }) {
  const phases = [...new Set(items.map((t) => t.milestone_name || t.phase || "Later in your program"))];
  return (
    <div className="space-y-5">{phases.map((p) => (
      <section key={p} data-testid={`portal-upcoming-phase-${p}`}>
        <h3 className="font-display text-sm font-semibold text-slate-700 mb-2">{p}</h3>
        <ul className="space-y-1.5">{items.filter((t) => (t.milestone_name || t.phase || "Later in your program") === p).map((t) => (
          <li key={t.id}><button onClick={t.read_only ? undefined : () => onOpen(t.id)} disabled={t.read_only} data-testid={`portal-task-${t.id}`}
            className="w-full text-left flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-slate-300">
            <CalendarRange className="w-4 h-4 text-slate-400 shrink-0" />{titleOf(t)}{trainingIds.has(t.id) && <TrainingBadge id={t.id} />}{t.owner === "district" && <span className="ml-auto text-[11px] text-slate-400">District Team</span>}
          </button></li>
        ))}</ul>
      </section>
    ))}</div>
  );
}

function TaskCard({ task, onOpen, training }) {
  const checked = (task.checklist || []).filter((c) => c.done).length;
  const cta = task.status === "needs_attention" ? "Upload Replacement" : task.needs_submission && !task.is_done && task.status !== "submitted" ? "Upload" : task.status === "in_progress" ? "Continue" : "View Task";
  return (
    <div data-testid={`portal-task-${task.id}`} className={`rounded-xl border bg-white p-4 transition-colors ${task.status === "needs_attention" ? "border-rose-200" : "border-slate-200"} ${onOpen ? "hover:border-[#0D9488]" : ""} ${task.is_done ? "opacity-80" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{titleOf(task)}</p>
          {task.summary && <p className="mt-1 text-sm text-slate-500 line-clamp-2">{task.summary}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {task.milestone_name ? <span data-testid={`portal-task-milestone-${task.id}`}>Milestone: <b className="font-medium text-slate-700">{task.milestone_name.replace(/^\s*[IVXLC]+\.\s+/, "")}</b></span>
              : task.phase && <span>Required for: <b className="font-medium text-slate-700">{task.phase}</b></span>}
            <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />{task.due_date ? `Due ${fmtDate(task.due_date)}` : "No deadline"}</span>
            {task.checklist?.length > 0 && <span>{checked}/{task.checklist.length} steps</span>}
            {task.owner === "district" && <span data-testid={`portal-task-owner-${task.id}`}>{task.staff_held ? task.owner_label : task.assignee_name ? `Assigned to ${task.assignee_name}` : task.owner_label}</span>}
          </div>
          {task.candidate_feedback && <p className="mt-2 text-xs text-rose-700 bg-rose-50 rounded-lg px-2.5 py-1.5" data-testid={`portal-task-feedback-${task.id}`}>{task.candidate_feedback}</p>}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <StatusPill t={task} testId={`portal-task-status-${task.id}`} />
          {training && <TrainingBadge id={task.id} />}
        </div>
      </div>
      {onOpen && <div className="mt-3"><Button size="sm" variant={task.status === "needs_attention" ? "default" : "outline"} onClick={onOpen} data-testid={`portal-task-open-${task.id}`} className={`rounded-full ${task.status === "needs_attention" ? "bg-rose-600 hover:bg-rose-700" : ""}`}>{cta === "Upload" || cta === "Upload Replacement" ? <Upload className="w-3.5 h-3.5 mr-1" /> : null}{cta}<ArrowRight className="w-3.5 h-3.5 ml-1" /></Button></div>}
    </div>
  );
}

const Section = ({ title, children, testId }) => (
  <section data-testid={testId}><h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</h3>{children}</section>
);

function TaskDetail({ taskId, onBack }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get(`/portal/tasks/${taskId}`).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); onBack(); }); }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-sm text-slate-400 py-8">Loading…</p>;
  const t = data.task;
  return (
    <div className="max-w-3xl space-y-6" data-testid="portal-task-dialog">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="portal-task-back"><ArrowLeft className="w-4 h-4" />Back to My Tasks</button>
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h2 className="font-display text-2xl font-bold text-slate-900" data-testid="portal-task-title">{titleOf(t)}</h2>
          <StatusPill t={t} testId="portal-task-detail-status" />
        </div>
        {!t.read_only && !t.is_planned && t.status !== "submitted" && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-600" data-testid="portal-task-status-control">Status: <PortalStatusSelect taskId={taskId} status={t.status} needsUpload={t.needs_submission && !t.is_done} onChanged={load} /></div>)}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
          {t.phase && <span>Required for: <b className="font-medium text-slate-700">{t.phase}</b></span>}
          <span>{t.due_date ? `Due ${fmtDate(t.due_date)}` : "No deadline"}</span>
        </div>
        {t.is_planned && <p className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-xl p-3" data-testid="portal-task-planned-note">This task isn't active yet. It will become available as you move through your program.</p>}
        {t.status === "needs_attention" && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="portal-task-needs-attention">
            <p className="font-semibold text-rose-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Needs Attention</p>
            <p className="text-sm text-rose-700 mt-1">{t.candidate_feedback || "Your district team needs additional information. Please upload a replacement below."}</p>
          </div>
        )}
        <div className="mt-6 space-y-6">
          {t.why_it_matters && <Section title="Why this matters" testId="portal-task-why"><p className="text-sm text-slate-700">{t.why_it_matters}</p></Section>}
          {t.description && <Section title="What you need to do" testId="portal-task-instructions">{t.description.includes("<") ? <RichTextView html={t.description} className="text-sm text-slate-700" /> : <p className="text-sm text-slate-700 whitespace-pre-wrap">{t.description}</p>}</Section>}
          <TaskTraining taskId={taskId} />
          <PortalTaskExtras taskId={taskId} onChanged={load} />
          {t.checklist?.length > 0 && <Steps t={t} />}
        </div>
      </div>
      <Messages taskId={taskId} data={data} setData={setData} />
    </div>
  );
}

function Steps({ t }) {
  return (
    <Section title="Steps" testId="portal-task-steps">
      <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">{t.checklist.map((c) => <li key={c.id} data-testid={`portal-step-${c.id}`}>{c.text}</li>)}</ul>
    </Section>
  );
}

function Messages({ taskId, data, setData }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try { const { data: c } = await api.post(`/portal/tasks/${taskId}/comments`, { body: comment.trim() }); setData((d) => ({ ...d, comments: [...d.comments, c] })); setComment(""); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Questions about this task?</p>
      <div className="mt-3 space-y-2">
        {data.comments.length === 0 && <p className="text-xs text-slate-400">No messages yet. Ask your district team a question below.</p>}
        {data.comments.map((c) => (
          <div key={c.id} className={`rounded-lg p-2.5 text-sm ${c.mine ? "bg-[#006090]/5 border border-[#006090]/15" : "bg-slate-50 border border-slate-200"}`} data-testid="portal-task-comment">
            <p className="text-[11px] font-semibold text-slate-500">{c.mine ? "You" : c.author_name}</p>
            <p className="text-slate-700 mt-0.5 whitespace-pre-wrap">{c.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Write a message…" data-testid="portal-comment-input" className="text-sm" />
        <Button onClick={send} disabled={busy || !comment.trim()} data-testid="portal-comment-send" className="bg-[#006090] hover:bg-[#004E75] self-end"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
